/**
 * Every route path and asset location in one place, so no component ever
 * builds a URL from a string literal.
 */

export const ROUTES = {
  home: '/',
  about: '/about',
  services: '/services',
  projects: '/projects',
  project: (slug: string) => `/projects/${slug}`,
  research: '/research',
  contact: '/contact',
  admin: '/admin',
} as const

/** Route patterns used by the router (distinct from the builders above). */
export const ROUTE_PATTERNS = {
  project: '/projects/:slug',
} as const

/**
 * Media lives in Cloudinary, under one folder per kind.
 *
 * The cloud name is not a secret — it is in every delivery URL — but it is
 * read from the environment so a fork or a second account needs no code
 * change. The server reads the same value from `api/.env`.
 */
const CLOUD = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME ?? 'dehgwgwuu'
const FOLDER = import.meta.env.VITE_CLOUDINARY_FOLDER ?? 'krisella-portfolio'

/**
 * No version in these URLs, unlike the ones the admin panel writes.
 *
 * These are compiled into the bundle, so a pinned version would mean a
 * deploy every time one of the built-in images is replaced in the media
 * library. `v1` delivers whatever is current, and uploads invalidate the
 * CDN copy, so swapping `portraits/portrait` in Cloudinary updates the site
 * without touching this repository.
 */
const IMAGE_BASE = `https://res.cloudinary.com/${CLOUD}/image/upload/f_auto,q_auto/v1/${FOLDER}`
const RAW_BASE = `https://res.cloudinary.com/${CLOUD}/raw/upload/v1/${FOLDER}`

/**
 * A picture, delivered in the best format the browser accepts.
 *
 * `f_auto,q_auto` is the reason these are not served from `public/`: the
 * portrait leaves Cloudinary at 63KB as WebP or AVIF against 91KB of
 * original JPEG, and neither the file nor this code has to change for that
 * to happen.
 */
export const asset = (path: string): string => `${IMAGE_BASE}/${path}`

/**
 * A file meant to be downloaded rather than rendered — the CV.
 *
 * Stored as a `raw` asset and delivered untransformed, extension and all,
 * so the browser saves a PDF rather than a nameless blob.
 */
export const download = (path: string): string => `${RAW_BASE}/${path}`
